import type { StytchError } from '@stytch/vanilla-js';
import { times } from 'lodash';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import type { TextInput } from 'react-native';
import {
    BodyLargeEmphasized,
    Input,
    Private,
    View,
    XStack,
    YStack,
} from 'smoothie';

import BaseOtpPage from '../../../../test/pages/auth/OtpPage';
import StytchErrorMessage from '../StytchErrorMessage';

const OTP_CODE_LENGTH = 6;

interface OTPInputProps {
    code: string;
    setCode: (string) => void;
    authError: StytchError | null;
    clearAuthError: () => void;
    onSubmitEditing: () => void;
}

const OTPInput = ({
    code,
    setCode,
    authError,
    clearAuthError,
    onSubmitEditing,
}: OTPInputProps) => {
    const otpInputRef = useRef<TextInput | null>(null);

    useEffect(() => {
        !!authError && otpInputRef.current?.focus();
    }, [!!authError]);

    const updateCode = (value: string) => {
        setCode(value);
        authError && clearAuthError();
    };

    const digitBox = (index: number) => {
        const isDigit = code[index] !== undefined;
        const digit = isDigit ? code[index] : '';

        const isCurrentValue = index === code.length;
        const isLastValue = index === OTP_CODE_LENGTH - 1;
        const isCodeComplete = code.length === OTP_CODE_LENGTH;

        const isValueFocused =
            isCurrentValue || (isLastValue && isCodeComplete);

        return (
            <View
                key={index}
                width={48}
                height={56}
                borderWidth={1}
                borderRadius={8}
                borderColor={
                    authError
                        ? isValueFocused
                            ? '$red1100'
                            : '$red600'
                        : isValueFocused
                          ? '$black'
                          : '$gray800'
                }
                alignItems="center"
                justifyContent="center"
                backgroundColor={authError ? '$red200' : '$white'}
            >
                <BodyLargeEmphasized
                    color={
                        authError
                            ? isDigit
                                ? '$red1100'
                                : '$red400'
                            : isDigit
                              ? '$black'
                              : '$gray600'
                    }
                >
                    {isDigit ? digit : 0}
                </BodyLargeEmphasized>
            </View>
        );
    };

    return (
        <YStack
            width="100%"
            gap={8}
            paddingHorizontal={16}
            alignItems="center"
            onPress={() => otpInputRef.current?.focus()}
        >
            <XStack justifyContent="space-between" width="100%" maxWidth={375}>
                {times(OTP_CODE_LENGTH, digitBox)}
            </XStack>

            <YStack minHeight={36} alignItems="center">
                {authError && (
                    <StytchErrorMessage
                        error={authError}
                        textAlign="center"
                        maxWidth="80%"
                    />
                )}
                <Private>
                    <Input
                        position="absolute"
                        opacity={0}
                        key="otp"
                        defaultValue={code}
                        onChangeText={updateCode}
                        maxLength={OTP_CODE_LENGTH}
                        textContentType="oneTimeCode"
                        keyboardType="number-pad"
                        testID={BaseOtpPage.otpInputId}
                        ref={otpInputRef}
                        autoCapitalize="none"
                        autoCorrect={false}
                        onSubmitEditing={onSubmitEditing}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                    />
                </Private>
            </YStack>
        </YStack>
    );
};

export default OTPInput;
